import * as React from 'react';
import { isMobileLayout } from '@hooks/useIsMobileLayout';
// The browser's close-request API (Android Chrome 120+), where there is one
const CloseWatcherApi = typeof window !== 'undefined' ? window.CloseWatcher : undefined;
let stack = [];
let nextId = 1;
// popstates we cause ourselves (removing an entry after a close), to ignore
let ownPops = 0;
// When the URL last changed: an overlay that opens right after it is one the URL opened
let urlChangedAt = Date.now();
const URL_OPENED_MS = 500;
if (typeof window !== 'undefined') {
    const wrap = (name) => {
        const original = window.history[name].bind(window.history);
        window.history[name] = ((state, unused, url) => {
            if (url !== undefined && url !== null && new URL(String(url), window.location.href).href !== window.location.href)
                urlChangedAt = Date.now();
            return original(state, unused, url);
        });
    };
    wrap('pushState');
    wrap('replaceState');
    window.addEventListener('popstate', () => {
        var _a, _b;
        if (ownPops > 0) {
            ownPops--;
            return;
        }
        urlChangedAt = Date.now();
        const top = stack[stack.length - 1];
        // Back went past the top overlay's entry: close it (only it)
        if ((top === null || top === void 0 ? void 0 : top.inHistory) && ((_a = window.history.state) === null || _a === void 0 ? void 0 : _a.uxpOverlay) !== top.id) {
            top.inHistory = false;
            (_b = top.onBack) === null || _b === void 0 ? void 0 : _b.call(top);
        }
    });
}
/**
 * Register in the overlay stack while `active`; returns a stable "am I the
 * topmost overlay?" checker for use inside global key handlers.
 * With `onClose`, Back on a phone calls it while this overlay is on top.
 */
export function useOverlayStack(active, onClose) {
    const idRef = React.useRef(0);
    const closeRef = React.useRef(onClose);
    closeRef.current = onClose;
    React.useEffect(() => {
        if (!active)
            return;
        const overlay = { id: nextId++, onBack: () => { var _a; return (_a = closeRef.current) === null || _a === void 0 ? void 0 : _a.call(closeRef); }, inHistory: false };
        idRef.current = overlay.id;
        stack.push(overlay);
        const openedByUrl = Date.now() - urlChangedAt < URL_OPENED_MS;
        if (closeRef.current && isMobileLayout() && !openedByUrl) {
            if (CloseWatcherApi) {
                const watcher = new CloseWatcherApi();
                watcher.onclose = () => { var _a; overlay.watcher = undefined; (_a = closeRef.current) === null || _a === void 0 ? void 0 : _a.call(closeRef); };
                overlay.watcher = watcher;
            }
            else {
                window.history.pushState(Object.assign(Object.assign({}, (window.history.state || {})), { uxpOverlay: overlay.id }), '');
                overlay.inHistory = true;
            }
        }
        return () => {
            var _a;
            stack = stack.filter((x) => x !== overlay);
            idRef.current = 0;
            if (overlay.watcher) {
                overlay.watcher.onclose = null;
                overlay.watcher.destroy();
            }
            // Closed some other way: take its entry off again (unless the app has navigated since)
            if (overlay.inHistory && ((_a = window.history.state) === null || _a === void 0 ? void 0 : _a.uxpOverlay) === overlay.id) {
                ownPops++;
                window.history.back();
            }
        };
    }, [active]);
    return React.useCallback(() => { var _a; return idRef.current !== 0 && ((_a = stack[stack.length - 1]) === null || _a === void 0 ? void 0 : _a.id) === idRef.current; }, []);
}
